import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Plus, ShieldCheck, UserPlus, X } from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { useRole } from "@/lib/auth/use-role";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  PageHeader,
  Spinner,
} from "@/components/ui/primitives";
import {
  addPerson,
  createCampaign,
  deleteCampaign,
  listAttestations,
  listCampaigns,
  setAttestationStatus,
  updateCampaign,
  type AttestationRow,
  type AttestationType,
  type CampaignRow,
} from "@/lib/api/conduct";
import { attestationProgress } from "@argus/engine";

const TYPE_LABEL: Record<AttestationType, string> = {
  code_of_ethics: "Code of ethics",
  holdings_report: "Holdings report",
  political_contribution: "Political contributions",
  custom: "Custom",
};

function EthicsInner() {
  const tenant = useTenant();
  const qc = useQueryClient();
  const { canWrite } = useRole(tenant.id);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ["attestation-campaigns", tenant.id],
    queryFn: () => listCampaigns(tenant.id),
  });

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: ["attestation-campaigns", tenant.id] });

  const selectedCampaign = campaigns?.find((c) => c.id === selected) ?? null;

  return (
    <>
      <PageHeader
        title="Code of ethics & attestations"
        subtitle="Run acknowledgement campaigns for the Code of Ethics, holdings reports, and conduct policies — track who has signed and chase the stragglers."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setCreating((c) => !c)}>
              <Plus className="size-4" /> New campaign
            </Button>
          ) : undefined
        }
      />

      {creating && canWrite && (
        <CreateCampaignForm
          tenantId={tenant.id}
          onDone={() => {
            invalidate();
            setCreating(false);
          }}
        />
      )}

      {isLoading ? (
        <p className="text-sm text-neutral-500">Loading…</p>
      ) : !campaigns || campaigns.length === 0 ? (
        <EmptyState
          icon={<ShieldCheck className="size-7" />}
          title="No attestation campaigns yet"
          description="Create a campaign — e.g. the annual Code of Ethics acknowledgement — add your supervised persons, and track signatures."
          action={
            canWrite ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                <Plus className="size-4" /> New campaign
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
          <div className="space-y-2">
            {campaigns.map((c) => (
              <CampaignCard
                key={c.id}
                campaign={c}
                selected={c.id === selected}
                canWrite={canWrite}
                onSelect={() => setSelected(c.id === selected ? null : c.id)}
                onClose={() => {
                  void updateCampaign(c.id, { status: "closed" }).then(invalidate);
                }}
                onDelete={() => {
                  if (confirm(`Delete campaign "${c.title}"?`)) {
                    void deleteCampaign(c.id).then(() => {
                      if (selected === c.id) setSelected(null);
                      invalidate();
                    });
                  }
                }}
              />
            ))}
          </div>
          {selectedCampaign ? (
            <RosterPanel campaign={selectedCampaign} canWrite={canWrite} />
          ) : (
            <Card className="hidden p-6 text-center text-xs text-neutral-500 lg:block">
              Select a campaign to manage its roster.
            </Card>
          )}
        </div>
      )}
    </>
  );
}

function CampaignCard({
  campaign,
  selected,
  canWrite,
  onSelect,
  onClose,
  onDelete,
}: {
  campaign: CampaignRow;
  selected: boolean;
  canWrite: boolean;
  onSelect: () => void;
  onClose: () => void;
  onDelete: () => void;
}) {
  const { data: roster } = useQuery({
    queryKey: ["attestations", campaign.id],
    queryFn: () => listAttestations(campaign.id),
  });
  const prog = attestationProgress(roster ?? []);
  return (
    <Card className={`px-4 py-3 ${selected ? "ring-brand-700/60 ring-1" : ""}`}>
      <button onClick={onSelect} className="w-full text-left">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-neutral-100">{campaign.title}</span>
          <Badge tone="neutral">{TYPE_LABEL[campaign.attestation_type]}</Badge>
          <Badge tone={campaign.status === "closed" ? "neutral" : "info"}>{campaign.status}</Badge>
          {campaign.due_date && (
            <span className="text-[11px] text-neutral-500">due {campaign.due_date}</span>
          )}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-neutral-800">
            <div
              className={`h-full rounded-full ${prog.complete ? "bg-emerald-500" : "bg-brand-500"}`}
              style={{ width: `${prog.pct}%` }}
            />
          </div>
          <span className="text-[11px] tabular-nums text-neutral-500">
            {prog.acknowledged}/{prog.total} signed
            {prog.exceptions > 0 ? ` · ${prog.exceptions} exception` : ""}
          </span>
        </div>
      </button>
      {canWrite && (
        <div className="mt-2 flex gap-1">
          {campaign.status !== "closed" && (
            <Button size="sm" variant="ghost" onClick={onClose}>
              Close
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onDelete}>
            Delete
          </Button>
        </div>
      )}
    </Card>
  );
}

function RosterPanel({ campaign, canWrite }: { campaign: CampaignRow; canWrite: boolean }) {
  const tenant = useTenant();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const { data: roster } = useQuery({
    queryKey: ["attestations", campaign.id],
    queryFn: () => listAttestations(campaign.id),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["attestations", campaign.id] });
    void qc.invalidateQueries({ queryKey: ["attestation-campaigns", tenant.id] });
  };

  const add = useMutation({
    mutationFn: () =>
      addPerson(tenant.id, campaign.id, {
        person_name: name.trim(),
        person_email: email.trim() || null,
      }),
    onSuccess: () => {
      setName("");
      setEmail("");
      invalidate();
    },
  });

  const setStatus = useMutation({
    mutationFn: ({ row, status }: { row: AttestationRow; status: AttestationRow["status"] }) =>
      setAttestationStatus(row, status),
    onSuccess: invalidate,
  });

  return (
    <Card className="self-start">
      <CardHeader title={`Roster — ${campaign.title}`} />
      <div className="divide-y divide-neutral-800/70">
        {(roster ?? []).length === 0 ? (
          <p className="px-4 py-5 text-center text-xs text-neutral-500">
            No people yet. {canWrite ? "Add supervised persons below." : ""}
          </p>
        ) : (
          (roster ?? []).map((r) => (
            <div key={r.id} className="flex items-center gap-2 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-neutral-200">{r.person_name}</div>
                {r.person_email && (
                  <div className="truncate text-[11px] text-neutral-500">{r.person_email}</div>
                )}
              </div>
              {r.status === "acknowledged" ? (
                <Badge tone="clear">
                  <Check className="size-3" /> Signed
                </Badge>
              ) : r.status === "exception" ? (
                <Badge tone="risk">Exception</Badge>
              ) : (
                <Badge tone="neutral">Pending</Badge>
              )}
              {canWrite && (
                <div className="flex gap-1">
                  {r.status !== "acknowledged" && (
                    <button
                      title="Mark signed"
                      onClick={() => setStatus.mutate({ row: r, status: "acknowledged" })}
                      className="rounded p-1 text-emerald-400 hover:bg-neutral-800"
                    >
                      <Check className="size-3.5" />
                    </button>
                  )}
                  {r.status !== "exception" && (
                    <button
                      title="Flag exception"
                      onClick={() => setStatus.mutate({ row: r, status: "exception" })}
                      className="rounded p-1 text-amber-400 hover:bg-neutral-800"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      {canWrite && (
        <div className="space-y-2 border-t border-neutral-800 p-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-2.5 py-1.5 text-sm text-neutral-200 placeholder:text-neutral-600"
          />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email (optional)"
            className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-2.5 py-1.5 text-sm text-neutral-200 placeholder:text-neutral-600"
          />
          <Button
            size="sm"
            variant="secondary"
            className="w-full"
            disabled={!name.trim() || add.isPending}
            onClick={() => add.mutate()}
          >
            <UserPlus className="size-3.5" /> Add person
          </Button>
          {add.error && <p className="text-[11px] text-red-300">{add.error.message}</p>}
        </div>
      )}
    </Card>
  );
}

function CreateCampaignForm({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const [title, setTitle] = useState("");
  const [type, setType] = useState<AttestationType>("code_of_ethics");
  const [due, setDue] = useState("");

  const create = useMutation({
    mutationFn: () =>
      createCampaign({
        tenant_id: tenantId,
        title: title.trim(),
        attestation_type: type,
        due_date: due || null,
      }),
    onSuccess: onDone,
  });

  const inputCls =
    "mt-1 w-full rounded-md border border-neutral-800 bg-neutral-900/60 px-2.5 py-1.5 text-sm text-neutral-200 placeholder:text-neutral-600";

  return (
    <Card className="space-y-3 p-4">
      <div className="grid gap-3 sm:grid-cols-[1fr_200px_160px]">
        <Field label="Title">
          <input
            className={inputCls}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="2026 Annual Code of Ethics acknowledgement"
          />
        </Field>
        <Field label="Type">
          <select
            className={inputCls}
            value={type}
            onChange={(e) => setType(e.target.value as AttestationType)}
          >
            {Object.entries(TYPE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Due date">
          <input
            type="date"
            className={inputCls}
            value={due}
            onChange={(e) => setDue(e.target.value)}
          />
        </Field>
      </div>
      {create.error && <p className="text-xs text-red-300">{create.error.message}</p>}
      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={!title.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? <Spinner className="size-4" /> : <Plus className="size-4" />}
          Create campaign
        </Button>
      </div>
    </Card>
  );
}

export function EthicsPage() {
  return (
    <TenantLayout>
      <EthicsInner />
    </TenantLayout>
  );
}
